import { useCallback, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import { Box, CircularProgress } from "@mui/material";
import {
  atom,
  useRecoilState,
  useSetRecoilState,
  selector,
  useRecoilValue,
} from "recoil";
import { Outlet, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useIntl } from "react-intl";

import { Industry } from "./Industry";
import { listMenus, Row } from "./Layout/utils";
import { checkErrors, getDefaultOptions } from "./services/Category";
import { favoritesAtom } from "./Layout/atom";
import { getMainStyle } from "./styles";
import { getCurrentLocale } from "./Locale";
import { logoutUser } from "./keycloak";
import { RevbellMenuItem } from "./Layout/Menu/Menu";
import {
  anonymizeCustomers,
  demoModeState,
  getRealCustomer,
} from "./services/demoMode";

const KEYCLOAK_APP = "revbell-app";
const HOSTNAME = window.location.hostname;

export const ROLES_MAP = {
  USER: "user",
  ADMIN: "admin",
  SUPER_ADMIN: "super_admin",
};

export enum Roles {
  User = "user",
  Admin = "admin",
  SuperAdmin = "super_admin",
  Pricer = "pricer",
}

interface TokenPayload {
  customer_names?: string;
  resource_access?: {
    [key: string]: {
      roles?: Roles[];
    };
  };
}

export interface Currency {
  code: string;
  symbol: string;
}

export interface Flags {
  cockpit_price_graph_min_Y_axis: number;
  enable_customer_competithive?: "0" | "1";
  enable_grid_type_uh_cat_mapping: "0" | "1";
  event_suggestions_enabled?: "0" | "1";
  event_view_depth_in_years?: number;
  is_active_autopilot?: "0" | "1";
  is_active_businessrules?: "0" | "1";
  is_new_competitor_management?: "0" | "1";
  is_new_price_management?: "0" | "1";
  is_restriction_on_grid_type_level?: "0" | "1";
  is_ponant?: "0" | "1";
  lead_time_min: number;
  list_booking_status_in_kpis?: string;
  price_ceiling: string;
  price_floor: string;
}

export interface Tenant {
  industry: Industry;
  currency: Currency;
  flags: Flags;
}

export interface BatchLog {
  ended_at: string;
  started_at: string;
}

export interface FavoriteItem {
  id: string;
  label: string;
}

export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  roles: string;
  username: string;
  is_staff: boolean;
  tenant: Tenant;
  last_successful_batch: BatchLog;
  profile_picture_url: string | null;
  menu_favorites: FavoriteItem[] | null;
}

function generateUniqueTrigramDict(
  customers: string[]
): Record<string, string> {
  const used = new Set<string>();
  const dict: Record<string, string> = {};
  customers.forEach((name) => {
    const base = name.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    let trigram = base.slice(0, 3);
    let i = 1;
    while (used.has(trigram)) {
      trigram = base.slice(0, 2) + base.slice(-1);
      if (used.has(trigram)) {
        trigram = base[0] + base.slice(-2);
      }
      if (used.has(trigram)) {
        trigram = base.slice(0, 2) + i;
        i++;
      }
    }
    used.add(trigram);
    dict[name] = trigram;
  });
  return dict;
}

const getCurrentUser = async () => {
  const url = new URL(`/api/users/me/`, window.origin);
  const response = await fetch(url.toString(), getDefaultOptions());

  checkErrors(response);
  const json: User = await response.json();
  return json;
};

const getEnabledCustomers = async (tokenData: TokenPayload | null) => {
  const hasSuperAdminRole = tokenData?.resource_access?.[
    KEYCLOAK_APP
  ]?.roles?.includes(Roles.SuperAdmin);

  if (!hasSuperAdminRole) {
    return tokenData?.customer_names?.split(";") ?? [];
  }

  const url = new URL(`/api/customers/?page_size=1000`, window.origin);
  const response = await fetch(url.toString(), getDefaultOptions());

  checkErrors(response);
  const json = await response.json();
  const customer_names: string[] = json["results"]["customer_infos"].map(
    (x: any) => x["customer_name"]
  );

  return customer_names;
};

export const useHasRole = () => {
  const tokenData = useRecoilValue(tokenDataAtom);

  const hasRole = useCallback(
    (role: Roles): boolean => {
      // Normalize case: the token roles aren't guaranteed lowercase (the
      // Django side defensively upper-cases them), and the Roles enum values
      // are lowercase. Compare case-insensitively so a "PRICER"/"Pricer"
      // token role still matches.
      const roles: string[] = (
        tokenData?.resource_access?.["revbell-app"]?.roles || [ROLES_MAP.USER]
      ).map((r) => r.toLowerCase());

      return roles.includes(Roles.SuperAdmin) || roles.includes(role);
    },
    [tokenData]
  );

  const hasAnyRole = useCallback(
    (roles: Roles[]): boolean => {
      if (roles.length === 0) {
        return hasRole(Roles.SuperAdmin);
      }

      return roles.some((role) => hasRole(role));
    },
    [hasRole]
  );

  return { hasRole, hasAnyRole };
};

export const currentUserAtom = atom<User | null>({
  key: "currentUser",
  default: null,
});

export const currentUserCurrencyAtom = selector<Currency>({
  key: "currentUserCurrency",
  get: ({ get }) => {
    const currentUser = get(currentUserAtom);

    return (
      currentUser?.tenant.currency || {
        code: "EUR",
        symbol: "€",
      }
    );
  },
});

export const lastBatchAtom = selector({
  key: "lastBatchAtom",
  get: ({ get }) => {
    const currentUser = get(currentUserAtom);

    return currentUser?.last_successful_batch;
  },
});

export const currentLocaleAtom = atom<string>({
  key: "currentLocale",
  default: "fr-FR",
});

const AppLoading = () => {
  return (
    <Box sx={{ ...getMainStyle }}>
      <Row
        sx={{
          alignItems: "center",
          justifyContent: "center",
          flex: "100%",
        }}
      >
        <CircularProgress />
      </Row>
    </Box>
  );
};

export const customerOptionsAtom = atom<string[]>({
  key: "customerOptions",
  default: [],
});

export const selectedCustomerAtom = atom<string | null>({
  key: "selectedCustomer",
  default: null,
});

export const tokenDataAtom = atom<TokenPayload | null>({
  key: "tokenData",
  default: null,
});

export const menuItemsAtom = atom<RevbellMenuItem[]>({
  key: "menuItems",
  default: [],
});

export const showCustomerDropdownAtom = selector<boolean>({
  key: "showCustomerDropdown",
  get: ({ get }) => {
    const customerOptions = get(customerOptionsAtom);
    const tokenData = get(tokenDataAtom);

    if (!tokenData) return false;

    return customerOptions.length > 1;
  },
});

const DEFAULT_FAVORITES_MENU: {
  slug: string;
  id: string;
  labelKey: string;
}[] = [
  {
    slug: "active_alerts",
    id: "/alerte/hebergements/",
    labelKey: "ALERTS_MENU",
  },
  // The cockpit follows the alert list's module.
  { slug: "active_alerts", id: "/cockpit/", labelKey: "COCKPIT_MENU" },
  { slug: "mass_pricing", id: "/calendar/", labelKey: "CALENDAR_MENU" },
  { slug: "pricing", id: "/price/", labelKey: "WORKFLOW_MENU" },
];

export const AuthProvider = () => {
  const [currentUser, setCurrentUser] = useRecoilState(currentUserAtom);
  const [customerOptions, setCustomerOptions] =
    useRecoilState(customerOptionsAtom);
  const setMenuItemsAtom = useSetRecoilState(menuItemsAtom);
  const setFavorites = useSetRecoilState(favoritesAtom);
  const intl = useIntl();
  const [selectedCustomer, setSelectedCustomer] =
    useRecoilState(selectedCustomerAtom);
  const setTokenData = useSetRecoilState(tokenDataAtom);
  const setLocale = useSetRecoilState(currentLocaleAtom);
  const { hasAnyRole } = useHasRole();

  const query = useQuery(["/users/me"], () => getCurrentUser(), {
    retry: false,
  });

  const menuItems = useQuery(["menu-items"], () => listMenus(), {
    onSuccess: (menus) => {
      const transformedData = menus.filter((item) =>
        hasAnyRole(item.allowedRoles)
      );
      setMenuItemsAtom(transformedData);
    },
  });

  const tokenData = useRecoilValue(tokenDataAtom);

  useQuery(["customers", tokenData], () => getEnabledCustomers(tokenData), {
    enabled: !!localStorage.getItem("access_token") && !!tokenData, // Wait for tokenData
    onSuccess: (customers) => {
      setCustomerOptions(customers);
      const storedCustomer = localStorage.getItem("selectedCustomer");
      let customerToSet = "";

      if (customers.length === 1) {
        customerToSet = customers[0] ?? "";
      } else if (customers.length > 1) {
        customerToSet =
          storedCustomer && customers.includes(storedCustomer)
            ? storedCustomer
            : customers[0] ?? "";
      }

      if (customerToSet) {
        setSelectedCustomer(customerToSet);
      }
    },
    onError: (error) => {
      console.error("Failed to fetch customers:", error);
    },
  });

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) {
      try {
        const decoded = jwtDecode<TokenPayload>(token);
        setTokenData(decoded);
      } catch (e) {
        console.error("Failed to decode token:", e);
      }
    }
  }, [setSelectedCustomer, setTokenData]);

  useEffect(() => {
    if (selectedCustomer) {
      // everytime we change the selected customer, we need to change the localStorage value and cookie as well (for php)
      localStorage.setItem("selectedCustomer", selectedCustomer);
      document.cookie = `customer-name=${selectedCustomer}; path=/; domain=${HOSTNAME}; SameSite=Lax`;
    }
  }, [selectedCustomer]);

  const location = useLocation();
  useEffect(() => {
    const storedCustomer = localStorage.getItem("selectedCustomer");
    if (!storedCustomer) return;
    if (
      customerOptions.length > 0 &&
      !customerOptions.includes(storedCustomer)
    ) {
      localStorage.removeItem("selectedCustomer");
      return;
    }
    if (storedCustomer !== selectedCustomer) {
      setSelectedCustomer(storedCustomer);
    }
  }, [
    location.pathname,
    selectedCustomer,
    setSelectedCustomer,
    customerOptions,
  ]);

  // Handle user data fetch
  useEffect(() => {
    if (!query.data) return;
    const user = query.data;

    setCurrentUser(user);
    setLocale(getCurrentLocale());
  }, [query.data, setCurrentUser, setLocale]);

  // Handle default favorites based on accessible menu items
  useEffect(() => {
    if (!query.data || !menuItems.data) return;
    const user = query.data;

    if (user.menu_favorites === null) {
      const accessibleSlugs = new Set(
        menuItems.data
          .filter((item) => hasAnyRole(item.allowedRoles))
          .map((item) => item.slug)
      );
      const defaults = DEFAULT_FAVORITES_MENU.filter((c) =>
        accessibleSlugs.has(c.slug)
      ).map((c) => ({
        id: c.id,
        label: intl.formatMessage({ id: c.labelKey }),
      }));
      setFavorites(defaults);
    } else {
      setFavorites(user.menu_favorites);
    }
  }, [query.data, menuItems.data, hasAnyRole, intl, setFavorites]);

  if (query.isError) {
    console.log(
      "Can not fetch user from the database, so logging out the current user"
    );
    logoutUser();
    return null;
  }

  if (query.isLoading || !currentUser || menuItems.isLoading) {
    return <AppLoading />;
  }

  return <Outlet />;
};

export const useCustomer = () => {
  const [selectedCustomer, setSelectedCustomer] =
    useRecoilState(selectedCustomerAtom);
  const demoMode = useRecoilValue(demoModeState);
  const customerOptions = useRecoilValue(customerOptionsAtom);
  const showDropdown = useRecoilValue(showCustomerDropdownAtom);

  const anonymizeMap = useMemo(() => {
    return demoMode ? anonymizeCustomers(customerOptions) : {};
  }, [demoMode, customerOptions]);

  const anonymizedCustomerOptions = demoMode
    ? customerOptions.map((c) => anonymizeMap[c] ?? c)
    : customerOptions;

  const anonymizedSelectedCustomer =
    demoMode && selectedCustomer
      ? anonymizeMap[selectedCustomer] ?? selectedCustomer
      : selectedCustomer;

  const anonymizeTrigramDict = demoMode
    ? generateUniqueTrigramDict(anonymizedCustomerOptions)
    : generateUniqueTrigramDict(customerOptions);

  const handleCustomerChange = (customer: string) => {
    const realCustomer = demoMode
      ? getRealCustomer(customer, anonymizeMap)
      : customer;
    if (realCustomer) {
      setSelectedCustomer(realCustomer);
      // Reload on the bare path, not the current URL: the query string holds
      // ids scoped to the customer we are leaving (the cockpit's `property` /
      // `uhTypeId`, a filtered list's selection…), and a plain reload would
      // carry them into the new one, where they point at nothing or — worse —
      // at an unrelated record that happens to share the id.
      window.location.assign(window.location.pathname);
    }
  };

  return {
    selectedCustomer,
    customerOptions: anonymizedCustomerOptions,
    customerTrigramDict: anonymizeTrigramDict,
    anonymizedSelectedCustomer,
    showDropdown,
    handleCustomerChange,
  };
};
